import { TabContext, TabList } from '@mui/lab';
import {
    Box,
    CircularProgress,
    DialogContent,
    DialogTitle,
    Tab,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import CustomDialog from 'components/custom/CustomDialog';
import CustomTableCollapsable from 'components/CustomTableCollapsable';
import dayjs from 'dayjs';
import HeaderOuBannerDependendoDaInterpretacaoH1 from 'layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1';
import { mascaraCpfCnpj } from 'pages/cpfNota/CpfNota';
import VisualizarItem from 'pages/financas/VisualizarItem';
import { IProdutoRow } from 'pages/registra-venda/registra-venda.model';
import { TVisualizarVenda } from 'pages/Venda/vendas.model';
import { useCallback, useState } from 'react';
import { theme } from 'theme';
import { ColumnData, currencyOf, isEmpty, toLocaleDateString } from 'utils/util';

export type TVisualizarVendaProps = {
    visualizar: TVisualizarVenda;
    onClose: () => void;
};

enum TVisualizacaoTabs {
    IDENTIFICACAO = 'Identificação',
    CLIENTE = 'Cliente',
    VENDEDORES = 'Vendedores',
    PRODUTOS = 'Produtos',
    TOTAIS = 'Totais',
    DADOS_PAGAMENTO = 'Dados de pagamento',
    OBSERVACOES = 'Observações',
    TRANSPORTADOR = 'Dados do transportador',
}

const initialState = {
    selectedTab: TVisualizacaoTabs.IDENTIFICACAO,
};

const VisualizarVenda = (props: TVisualizarVendaProps) => {
    const [stateLocal, setStateLocal] = useState(initialState);

    const isTabIdentificacao = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.IDENTIFICACAO;
    }, []);

    const isTabCliente = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.CLIENTE;
    }, []);

    const isTabTransportador = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.TRANSPORTADOR;
    }, []);

    const isTabVendedores = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.VENDEDORES;
    }, []);

    const isTabProdutos = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.PRODUTOS;
    }, []);

    const isTabTotais = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.TOTAIS;
    }, []);

    const isTabDadosPagamento = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.DADOS_PAGAMENTO;
    }, []);

    const isTabObservacao = useCallback((selected: TVisualizacaoTabs) => {
        return selected === TVisualizacaoTabs.OBSERVACOES;
    }, []);

    const onChangeTab = useCallback((_: unknown, value: TVisualizacaoTabs) => {
        setStateLocal(prevState => ({
            ...prevState,
            selectedTab: value,
        }));
    }, []);

    const columns: Array<ColumnData<IProdutoRow>> = [
        {
            id: 'produto-codigo',
            header: 'Código',
            key: 'produto.codigoSku',
            type: 'string',
            width: '10%',
            textAlign: 'left',
        },
        {
            id: 'produto-descricao',
            header: 'Nome / Cor / Tamanho',
            key: 'produto.descritivo',
            type: 'string',
            width: '30%',
            textAlign: 'left',
        },
        {
            id: 'produto-valor-unitario',
            header: 'Valor unitário',
            key: 'produto.preco',
            type: 'currency',
            width: '10%',
            textAlign: 'right',
        },
        {
            id: 'produto-desconto-percentual',
            header: '% desconto',
            key: 'itemDesconto.percentualDesconto',
            type: 'percent',
            width: '12%',
            textAlign: 'center',
        },
        {
            id: 'produto-desconto-valor',
            header: 'Valor de desconto',
            key: 'itemDesconto.valorDesconto',
            type: 'currency',
            width: '10%',
            textAlign: 'center',
        },
        {
            id: 'produto-quantidade-total',
            header: 'Quantidade total',
            key: 'quantidade',
            type: 'unidadeMedida',
            width: '12%',
            textAlign: 'center',
            unidadeMedida: 'PC',
        },
        {
            id: 'produto-valor-total',
            header: 'Valor total',
            key: 'valorTotal',
            type: 'currency',
            width: '15%',
            textAlign: 'right',
        },
    ];

    const shouldRenderTab = (tab: TVisualizacaoTabs) => {
        const data = props.visualizar.data;

        if (!data) return false;

        switch (tab) {
            case TVisualizacaoTabs.TRANSPORTADOR:
                return !!data.transportador?.frete;

            case TVisualizacaoTabs.OBSERVACOES:
                return !!(data.observacao || data.observacaoFixa);

            case TVisualizacaoTabs.CLIENTE:
                return !!data.cliente;

            case TVisualizacaoTabs.VENDEDORES:
                return !!data.vendedorPrincipal;

            default:
                return true;
        }
    };

    return (
        <CustomDialog
            open
            onClose={props.onClose}
            maxWidth='xl'
            PaperProps={{
                sx: {
                    height: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '70vw',
                },
            }}>
            <DialogTitle sx={{ textAlign: 'center' }}>
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='movimentacoes'
                    iconClassName='fa-solid fa-eye'
                    label='Visualizar'
                    small
                />
            </DialogTitle>

            <DialogContent>
                <TabContext value={stateLocal.selectedTab}>
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            overflow: 'hidden',
                        }}>
                        <TabList
                            onChange={onChangeTab}
                            variant='scrollable'
                            scrollButtons='auto'
                            visibleScrollbar
                            sx={{
                                width: '100%',
                                '& .MuiTabs-scroller': {
                                    overflowX: 'auto',
                                    justifyItems: 'center',
                                    alignItems: 'center',
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}>
                            {Object.values(TVisualizacaoTabs).map(tab =>
                                shouldRenderTab(tab) ?
                                    <Tab
                                        key={tab}
                                        label={tab}
                                        value={tab}
                                        disabled={!props.visualizar.data}
                                    />
                                :   null,
                            )}
                        </TabList>
                    </Box>
                </TabContext>

                {!!props.visualizar.data && (
                    <Box sx={{ marginTop: '30px' }}>
                        {isTabIdentificacao(stateLocal.selectedTab) && (
                            <>
                                <VisualizarItem
                                    title='Pedido'
                                    item={props.visualizar.data.numero}
                                />
                                <VisualizarItem
                                    title='Entrega'
                                    item={props.visualizar.data.numeroEntrega}
                                />
                                <VisualizarItem
                                    title='Finalidade de pedido'
                                    item={props.visualizar.data.finalidadePedido?.descritivo}
                                />
                                <VisualizarItem
                                    title='Tabela de preço'
                                    item={props.visualizar.data.tabelaPreco?.descritivo}
                                />
                                <VisualizarItem
                                    title='Depósito'
                                    item={props.visualizar.data.deposito?.descritivo}
                                />
                                <VisualizarItem
                                    title='Status'
                                    item={props.visualizar.data.statusPedido}
                                />
                                <VisualizarItem
                                    title='Data de emissão'
                                    item={toLocaleDateString(props.visualizar.data.emissao)}
                                />
                                <VisualizarItem
                                    title='Canal de venda'
                                    item={props.visualizar.data.canalDeVenda?.descritivo}
                                />
                            </>
                        )}

                        {isTabCliente(stateLocal.selectedTab) && (
                            <>
                                <VisualizarItem
                                    title='Identificação'
                                    item={mascaraCpfCnpj(
                                        props.visualizar.data.cliente?.identificacao ?? '',
                                    )}
                                />
                                <VisualizarItem
                                    title='Razão social'
                                    item={props.visualizar.data.cliente?.nome}
                                />
                                <VisualizarItem
                                    title='Nome fantasia'
                                    item={props.visualizar.data.cliente?.nomeFantasia}
                                />

                                <VisualizarItem
                                    title='E-mail'
                                    item={props.visualizar.data.cliente?.email}
                                />
                            </>
                        )}

                        {isTabTransportador(stateLocal.selectedTab) && (
                            <>
                                <VisualizarItem
                                    title='Frete'
                                    item={
                                        props.visualizar.data.transportador?.frete?.descricao ?? ''
                                    }
                                />
                                <VisualizarItem
                                    title='Transportadora'
                                    item={
                                        props.visualizar.data.transportador?.transportadora
                                            ?.descritivo ?? ''
                                    }
                                />
                                <VisualizarItem
                                    title='Placa'
                                    item={props.visualizar.data.transportador?.placa ?? ''}
                                />
                                <VisualizarItem
                                    title='UF'
                                    item={props.visualizar.data.transportador?.uf ?? ''}
                                />
                                <VisualizarItem
                                    title='Quantidade de volumes'
                                    item={props.visualizar.data.transportador?.quantidade ?? ''}
                                />
                                <VisualizarItem
                                    title='Espécie dos volumes'
                                    item={props.visualizar.data.transportador?.especie ?? ''}
                                />
                                <VisualizarItem
                                    title='Marca dos volumes'
                                    item={props.visualizar.data.transportador?.marca ?? ''}
                                />
                                <VisualizarItem
                                    title='Número dos volumes'
                                    item={props.visualizar.data.transportador?.numeroVolumes ?? ''}
                                />
                                <VisualizarItem
                                    title='Peso Líquido'
                                    item={props.visualizar.data.transportador?.pesoLiquido
                                        ?.toFixed(3)
                                        .replace('.', ',')
                                        .concat(' Kg')}
                                />
                                <VisualizarItem
                                    title='Peso Bruto'
                                    item={props.visualizar.data.transportador?.pesoBruto
                                        ?.toFixed(3)
                                        .replace('.', ',')
                                        .concat(' Kg')}
                                />
                                <VisualizarItem
                                    title='Valor do frete'
                                    item={currencyOf(
                                        props.visualizar.data.transportador?.valorFrete,
                                    )}
                                />
                                <VisualizarItem
                                    title='Valor do seguro'
                                    item={currencyOf(
                                        props.visualizar.data.transportador?.valorSeguro,
                                    )}
                                />
                            </>
                        )}

                        {isTabVendedores(stateLocal.selectedTab) && (
                            <>
                                <VisualizarItem
                                    title='Vendedor principal'
                                    item={props.visualizar.data.vendedorPrincipal?.descritivo}
                                />
                                <VisualizarItem
                                    title='Forma de comissão'
                                    item={props.visualizar.data.formaComissao?.descritivo}
                                />

                                {!isEmpty(props.visualizar.data.outrosVendedores) && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main,
                                                textAlign: 'center',
                                                width: '100%',
                                                marginTop: '20px',
                                                marginBottom: '20px',
                                            }}>
                                            Outros Comissionados
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                            }}>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Comissionado
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Papel
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Forma de comissão
                                            </Typography>
                                        </Box>

                                        {props.visualizar.data.outrosVendedores.map(
                                            outroComissionado => {
                                                return (
                                                    <Box
                                                        key={outroComissionado.uuid}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            justifyContent: 'space-between',
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                fontSize: '16px',
                                                                width: '33%',
                                                            }}>
                                                            {outroComissionado.descritivo}
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                fontSize: '16px',
                                                                width: '33%',
                                                            }}>
                                                            COMISSIONADO
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                fontSize: '16px',
                                                                width: '33%',
                                                            }}>
                                                            {outroComissionado.formaComissao}
                                                        </Typography>
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </>
                                )}

                                {!isEmpty(props.visualizar.data.guias) && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main,
                                                textAlign: 'center',
                                                width: '100%',
                                                marginTop: '20px',
                                                marginBottom: '20px',
                                            }}>
                                            Guias
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                            }}>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Guia
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Papel
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'justify',
                                                    fontWeight: 'bold',
                                                    fontSize: '16px',
                                                    width: '33%',
                                                }}>
                                                Forma de comissão
                                            </Typography>
                                        </Box>

                                        {props.visualizar.data.guias.map(guia => {
                                            return (
                                                <Box
                                                    key={guia.uuid}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'space-between',
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            textAlign: 'justify',
                                                            fontSize: '16px',
                                                            width: '33%',
                                                        }}>
                                                        {guia.descritivo}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            textAlign: 'justify',
                                                            fontSize: '16px',
                                                            width: '33%',
                                                        }}>
                                                        GUIA
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            textAlign: 'justify',
                                                            fontSize: '16px',
                                                            width: '33%',
                                                        }}>
                                                        {guia.formaComissao}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </>
                                )}
                            </>
                        )}

                        {isTabProdutos(stateLocal.selectedTab) && (
                            <>
                                <TableRow
                                    sx={{
                                        width: '100%',
                                        display: 'inline-table',
                                        tableLayout: 'fixed',
                                    }}>
                                    {columns.map(item => {
                                        return (
                                            <TableCell
                                                key={item.key}
                                                sx={{
                                                    width: item.width,
                                                    fontWeight: 'bold',
                                                    padding: 0,
                                                    bgcolor: theme.palette.background.default,
                                                }}
                                                align={item.textAlign}>
                                                {item.header}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>

                                <Box
                                    sx={{
                                        maxHeight: '400px',
                                        overflow: 'hidden',
                                        overflowY: 'auto',
                                    }}>
                                    {props.visualizar.data.itens.map(item => {
                                        item.produto.descritivo = item.produto.descritivo.replace(
                                            item.produto.codigoSku.concat(' - '),
                                            '',
                                        );
                                        return (
                                            <CustomTableCollapsable
                                                key={item.uuid}
                                                colapsadas={[]}
                                                onToggle={() => {}}
                                                item={item}
                                                columns={columns}
                                                children={null}
                                                columnsChildren={[]}
                                                toFilter=''
                                            />
                                        );
                                    })}
                                </Box>

                                <TableRow
                                    sx={{
                                        width: '100%',
                                        display: 'inline-table',
                                        marginY: '20px',
                                    }}>
                                    <TableCell
                                        sx={{
                                            width: '72%',
                                            minWidth: '72%',
                                            maxWidth: '72%',
                                            fontWeight: 'bold',
                                            paddingY: 0,
                                            color: theme.palette.primary.main,
                                        }}
                                        align={'left'}>
                                        {props.visualizar.data.itens.length} Produtos
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            width: '12%',
                                            minWidth: '12%',
                                            maxWidth: '12%',
                                            fontWeight: 'bold',
                                            paddingY: 0,
                                            paddingLeft: '20px',
                                            color: theme.palette.primary.main,
                                        }}
                                        align={'center'}>
                                        {props.visualizar.data.itens.reduce(
                                            (prev, act) => prev + act.quantidade,
                                            0,
                                        )}{' '}
                                        PC
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            width: '15%',
                                            minWidth: '15%',
                                            maxWidth: '15%',
                                            fontWeight: 'bold',
                                            paddingY: 0,
                                            color: theme.palette.primary.main,
                                        }}
                                        align={'right'}>
                                        {currencyOf(
                                            props.visualizar.data.itens.reduce(
                                                (prev, act) =>
                                                    prev +
                                                    (act.produto.preco * act.quantidade -
                                                        act.itemDesconto.valorDesconto),
                                                0,
                                            ),
                                        )}
                                    </TableCell>
                                </TableRow>
                            </>
                        )}

                        {isTabTotais(stateLocal.selectedTab) && (
                            <>
                                <VisualizarItem
                                    title='Valor total'
                                    item={currencyOf(props.visualizar.data.valorTotalBruto)}
                                />
                                <VisualizarItem
                                    title='Desconto em R$'
                                    item={currencyOf(
                                        props.visualizar.data.pedidoDescontoAcrescimo.valorDesconto,
                                    )}
                                />
                                <VisualizarItem
                                    title='Desconto em %'
                                    item={`${props.visualizar.data.pedidoDescontoAcrescimo.percentualDesconto.toFixed(2).replace('.', ',')} %`}
                                />
                                <VisualizarItem
                                    title='Acréscimo em R$'
                                    item={currencyOf(
                                        props.visualizar.data.pedidoDescontoAcrescimo.acrescimo,
                                    )}
                                />
                                <VisualizarItem
                                    title='Valor liquido'
                                    item={currencyOf(props.visualizar.data.valorTotalLiquido)}
                                />
                            </>
                        )}

                        {isTabDadosPagamento(stateLocal.selectedTab) && (
                            <>
                                {(() => {
                                    type ParcelaRow = {
                                        key: string;
                                        descritivo: string;
                                        numeroDeDias: number | null;
                                        vencimento: string | Date | null;
                                        valor: number;
                                    };

                                    const parcelaRows: Array<ParcelaRow> =
                                        props.visualizar.data.itensPagamento.flatMap(
                                            (itemPagamento, indexPagamento): Array<ParcelaRow> => {
                                                const numeroParcelas =
                                                    itemPagamento.infoAdicional?.numeroParcelas ??
                                                    1;

                                                if (numeroParcelas <= 1) {
                                                    return [
                                                        {
                                                            key: `${indexPagamento}-0`,
                                                            descritivo:
                                                                itemPagamento.formaPagamento
                                                                    .descritivo,
                                                            numeroDeDias:
                                                                itemPagamento.numeroDeDias,
                                                            vencimento: itemPagamento.vencimento,
                                                            valor: itemPagamento.valor,
                                                        },
                                                    ];
                                                }

                                                const valorCentavos = Math.round(
                                                    itemPagamento.valor * 100,
                                                );
                                                const sobraCentavos =
                                                    valorCentavos % numeroParcelas;
                                                const valorRateadoCentavos =
                                                    (valorCentavos - sobraCentavos) /
                                                    numeroParcelas;

                                                return Array.from(
                                                    { length: numeroParcelas },
                                                    (_, index) => {
                                                        const isUltima =
                                                            index === numeroParcelas - 1;
                                                        const numeroDeDias = 30 * (index + 1);
                                                        return {
                                                            key: `${indexPagamento}-${index}`,
                                                            descritivo: `${itemPagamento.formaPagamento.descritivo} (${index + 1}/${numeroParcelas})`,
                                                            numeroDeDias,
                                                            vencimento:
                                                                itemPagamento.vencimento ?
                                                                    dayjs(itemPagamento.vencimento)
                                                                        .add(numeroDeDias, 'day')
                                                                        .toISOString()
                                                                :   null,
                                                            valor:
                                                                (isUltima ?
                                                                    valorRateadoCentavos +
                                                                    sobraCentavos
                                                                :   valorRateadoCentavos) / 100,
                                                        };
                                                    },
                                                );
                                            },
                                        );

                                    const totalParcelas = parcelaRows.length;

                                    return (
                                        <>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        textAlign: 'justify',
                                                        fontWeight: 'bold',
                                                        fontSize: '16px',
                                                        minWidth: '50%',
                                                    }}>
                                                    Forma de pagamento
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        textAlign: 'justify',
                                                        fontWeight: 'bold',
                                                        fontSize: '16px',
                                                        minWidth: '15%',
                                                    }}>
                                                    Número de dias
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        textAlign: 'justify',
                                                        fontWeight: 'bold',
                                                        fontSize: '16px',
                                                        minWidth: '15%',
                                                    }}>
                                                    Vencimento
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        textAlign: 'right',
                                                        fontWeight: 'bold',
                                                        fontSize: '16px',
                                                        minWidth: '20%',
                                                    }}>
                                                    Valor
                                                </Typography>
                                            </Box>

                                            {parcelaRows.map(parcelaRow => {
                                                return (
                                                    <Box
                                                        key={parcelaRow.key}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            justifyContent: 'space-between',
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                minWidth: '50%',
                                                            }}>
                                                            {parcelaRow.descritivo}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                minWidth: '15%',
                                                            }}>
                                                            {parcelaRow.numeroDeDias}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'justify',
                                                                minWidth: '15%',
                                                            }}>
                                                            {parcelaRow.vencimento ?
                                                                toLocaleDateString(
                                                                    parcelaRow.vencimento,
                                                                )
                                                            :   ''}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'right',
                                                                minWidth: '20%',
                                                            }}>
                                                            {currencyOf(parcelaRow.valor)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    borderTop: `1px solid ${theme.palette.primary.main}`,
                                                    marginTop: '10px',
                                                }}>
                                                <Typography sx={{ minWidth: '50%' }} />
                                                <Typography sx={{ minWidth: '15%' }} />
                                                <Typography
                                                    sx={{
                                                        minWidth: '15%',
                                                        fontWeight: 'bold',
                                                        color: theme.palette.primary.main,
                                                    }}>
                                                    {totalParcelas}{' '}
                                                    {totalParcelas === 1 ? ' Parcela' : 'Parcelas'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        minWidth: '20%',
                                                        textAlign: 'right',
                                                        color: theme.palette.primary.main,
                                                    }}>
                                                    {currencyOf(
                                                        parcelaRows.reduce(
                                                            (prev, act) => prev + act.valor,
                                                            0,
                                                        ),
                                                    )}
                                                </Typography>
                                            </Box>
                                        </>
                                    );
                                })()}
                            </>
                        )}

                        {isTabObservacao(stateLocal.selectedTab) && (
                            <>
                                {!!props.visualizar.data.observacao && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main,
                                            }}>
                                            Observação geral:
                                        </Typography>

                                        <Typography>{props.visualizar.data.observacao}</Typography>
                                    </>
                                )}

                                {!!props.visualizar.data.observacaoFixa && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main,
                                            }}>
                                            Observação do cliente:
                                        </Typography>

                                        <Typography>
                                            {props.visualizar.data.observacaoFixa}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {!props.visualizar.data && (
                    <Box
                        sx={{
                            marginTop: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <CircularProgress
                            size={48}
                            color='primary'
                        />
                    </Box>
                )}
            </DialogContent>
        </CustomDialog>
    );
};

export default VisualizarVenda;
